<template>
    <IonContent fullscreen>
        <div class="absolute inset-x-0 z-10 pt-(--beyond-safe-area-inset-top)">
            <div class="min-h-header font-medium text-md flex items-center justify-between px-2">
                <div
                    class="h-[38px] px-2 rounded-full bg-white gap-2 flex items-center"
                >
                    <button
                        type="button"
                        class="appearance-none"
                        aria-label="Close"
                        @click="close"
                    >
                        <PhIcon
                            v-if="isIos"
                            icon="caret-left"
                            weight="light"
                            :size="24"
                        />
                        <PhIcon
                            v-else
                            icon="arrow-left"
                            weight="light"
                            :size="24"
                        />
                    </button>
                    <span
                        v-if="gallery.images.length > 1"
                        aria-live="polite"
                        aria-atomic="true"
                        class="mr-2"
                    >
                        {{ currentIndex + 1 }}/{{ gallery.images.length }}
                    </span>
                </div>
                <button
                    v-if="showContextMenu"
                    type="button"
                    :id="modalUuid"
                    class="ml-auto appearance-none flex items-center justify-center w-[38px] h-[38px] rounded-full bg-white"
                    aria-label="More options"
                    data-cy="image-context-menu"
                >
                    <PhIcon
                        icon="dots-three-vertical"
                        weight="bold"
                        :size="24"
                    />
                </button>
            </div>
        </div>

        <Swiper
            class="ds-image-viewer"
            :modules="modules"
            :initial-slide="gallery.start"
            :style="{
                '--swiper-navigation-color': '#fff',
            }"
            zoom
            loop
            keyboard
            @slide-change="onSlideChange"
        >
            <SwiperSlide
                v-for="(image, index) in gallery.images"
                :key="index"
            >
                <div class="swiper-zoom-container">
                    <img
                        :src="image.url"
                        :alt="image.description"
                    >
                </div>
            </SwiperSlide>
        </Swiper>
    </IonContent>

    <IonModal
        ref="modal"
        :trigger="modalUuid"
        :initial-breakpoint="1"
        :breakpoints="[0, 1]"
        class="ds-modal-bottom-sheet"
    >
        <div class="mt-6 mb-2 pb-(--beyond-safe-area-inset-bottom)">
            <UISimple
                title="Save image"
                :icon="{ icon: 'image', icon_url: null, color: null }"
                :right-icon="{ icon: 'caret-right', icon_url: null, color: null }"
                :border="false"
                @click="saveCurrentImage"
            />
        </div>
    </IonModal>
</template>

<script setup>
    import { v4 as uuidv4 } from 'uuid'
    import { Swiper, SwiperSlide } from 'swiper/vue'
    import { Keyboard, Zoom, Navigation } from 'swiper/modules'
    import { Capacitor } from '@capacitor/core'
    import { Media } from '@capacitor-community/media'
    import { modalController } from '@ionic/vue'

    import { statusBar } from '@/services'

    import 'swiper/css'
    import 'swiper/css/zoom'
    import 'swiper/css/navigation'
    import 'swiper/css/pagination'
    import PhIcon from '@/components/app/PhIcon.vue'
    import UISimple from '@/components/ui/ListItemLayouts/UISimple.vue'

    const modalUuid = ref(uuidv4())

    const props = defineProps({
        gallery: Object,
    })

    const deviceStore = useDeviceStore()
    const appStore = useAppStore()

    const { isIos } = storeToRefs(deviceStore)
    const { nativeIdentity } = storeToRefs(appStore)

    const { snackbar } = useSnackbars()

    const modules = [Keyboard, Zoom, Navigation]

    const currentIndex = ref(0)
    const mediaPluginAvailable = ref(Capacitor.isPluginAvailable('Media'))
    const modal = ref(null)

    const appName = computed(() => nativeIdentity.value?.appName ?? 'School App')

    // Android albums can't have special characters
    const safeAppName = computed(() => appName.value.replace(/[^a-zA-Z0-9 -]/g, ''))

    const showContextMenu = computed(() => {
        return mediaPluginAvailable.value || window.cypress
    })

    const onSlideChange = (swiper) => {
        currentIndex.value = swiper.realIndex
    }

    const close = () => {
        modalController.dismiss()
    }

    const getAlbumIdentifier = async (search) => {
        return (await Media.getAlbums()).albums.find((album) => album.name === search)?.identifier
    }

    async function saveCurrentImage() {
        if (!mediaPluginAvailable.value) {
            return
        }

        const url = props.gallery.images[currentIndex.value].url

        // Android devices require an album identifier to save images
        let albumIdentifier = null
        if (!isIos.value) {
            albumIdentifier = await getAlbumIdentifier(safeAppName.value)
            if (!albumIdentifier) {
                await Media.createAlbum({ name: safeAppName.value })
                albumIdentifier = await getAlbumIdentifier(safeAppName.value)
            }
        }

        Media.savePhoto({ path: url, albumIdentifier })
            .then(() => {
                snackbar({
                    message: 'Image saved',
                    type: 'success',
                })
            })
            .catch((e) => {
                console.error('error', e)
                snackbar({
                    message: 'Error saving image',
                    type: 'error',
                })
            }).finally(() => {
                modal.value.$el.dismiss()
            })
    }

    onBeforeMount(() => {
        statusBar.hide()
    })

    onBeforeUnmount(() => {
        statusBar.show()
    })
</script>

<style>
    .ds-image-viewer {
        width: 100%;
        height: 100%;
    }
</style>
